import api from '@/services/utils/apiInstance.ts';

import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type {
  ReferralAllocationResponseApi,
  ReferralReward,
  ReferralRewardFilters,
  ReferralRewardResponseApi,
  ReferralRewardSummary,
  ReferralUser,
  ReferralUserResponseApi
} from '@/types/services/referralReward/referralReward.type.ts';

function mapUser(user: ReferralUserResponseApi): ReferralUser {
  return {
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email
  }
}

function mapReward(data: ReferralRewardResponseApi): ReferralReward {
  return {
    id: data.id,
    status: data.status,
    amount: data.amount,
    attempts: data.attempts,
    lastError: data.last_error ?? null,
    createdAt: data.created_at,
    paidAt: data.paid_at ?? null,
    notifiedAt: data.notified_at ?? null,
    leaseUntil: data.lease_until ?? null,
    referrer: mapUser(data.referrer),
    referee: mapUser(data.referee),
    allocations: (data.allocations ?? []).map((a: ReferralAllocationResponseApi) => ({
      id: a.id,
      mandateId: a.mandate_id,
      mandateName: a.mandate_name,
      amount: a.amount,
      status: a.status,
      sharePrice: a.share_price ?? null,
      investedAt: a.invested_at ?? null,
      userTransactionId: a.user_transaction_id ?? null
    })),
    resolvedAt: data.resolved_at ?? null,
    resolvedById: data.resolved_by_id ?? null,
    resolutionNote: data.resolution_note ?? null
  }
}

class ReferralRewardService {
  // Most recent first.
  async getPaginated(
    page: number,
    limit: number,
    filters: ReferralRewardFilters
  ): Promise<PaginatedResponse<ReferralReward>> {
    const query = new URLSearchParams()
    query.set('page', String(page))
    query.set('limit', String(limit))
    if (filters.status) query.set('status', filters.status)
    if (filters.referrerId != null) query.set('referrerId', String(filters.referrerId))
    if (filters.refereeId != null) query.set('refereeId', String(filters.refereeId))

    const response = await api.get(`/referral-reward/bo?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((reward: ReferralRewardResponseApi) => mapReward(reward))
    }
  }

  // Every status is present, with 0 when there's none.
  async getSummary(): Promise<ReferralRewardSummary> {
    const response = await api.get('/referral-reward/bo/summary')
    return {
      totalCount: response.data.total_count,
      totalAmount: response.data.total_amount,
      statuses: response.data.statuses
    }
  }

  async get(rewardId: number): Promise<ReferralReward> {
    const response = await api.get(`/referral-reward/bo/${rewardId}`)
    return mapReward(response.data)
  }

  // Only valid on a FAILED reward (409 otherwise). Resumes where the job stopped.
  async retry(rewardId: number): Promise<ReferralReward> {
    const response = await api.post(`/referral-reward/bo/${rewardId}/retry`)
    return mapReward(response.data)
  }

  // Valid on REVIEW or FAILED (409 otherwise). Moves no money.
  async resolve(rewardId: number, note: string): Promise<ReferralReward> {
    const response = await api.post(`/referral-reward/bo/${rewardId}/resolve`, { note })
    return mapReward(response.data)
  }
}

export default new ReferralRewardService();
